import clsx from "clsx"
import { useState } from "react"
import { Controller, type FieldValues } from "react-hook-form"
import Calendar, { type RangePickerProps, type SinglePickerProps } from "@/shared/components/common/date-picker"
import { tCommon } from "@/shared/config/i18n"
import type { IFormControl } from "@/shared/types"
import { getMonthName, pack } from "@/shared/utils"

const getInputValue = (date?: Date | null) => {
  return date == null ? "" : `${pack(date?.getDate())} ${getMonthName(date)}, ${date.getFullYear()}`
}

interface DateRangePickerProps extends RangePickerProps {
  error?: string
  label?: string
  required?: boolean
  wrapperClass?: string
  minWidth?: number
}

interface DateSinglePickerProps extends SinglePickerProps {
  error?: string
  label?: string
  required?: boolean
  wrapperClass?: string
  minWidth?: number
  placeholder?: string
}

const DatePickerRange = ({ value, required, filter, monthsShow = 2, onSelect, label, error, wrapperClass }: DateRangePickerProps) => {
  const [show, setShow] = useState(false)
  const isAllRange = (value?.length ?? 0) > 1 && value?.every(date => date != null)
  return (
    <Calendar.Float
      show={show}
      onShowChange={setShow}
      showClear={isAllRange}
      onClear={() => onSelect?.([])}
      label={label}
      error={error}
      required={required}
      wrapperClass={wrapperClass}
      target={
        <div className="flex h-[18px] min-w-[300px] select-none items-center gap-2 text-sm">
          <span className={clsx(value?.[0] != null ? "text-text-color" : "text-gray-500")}>
            {value?.[0] != null ? getInputValue(value[0]) : "DD Month, YYYY"}
          </span>
          <span className={clsx(isAllRange ? "text-text-color" : "text-gray-500")}>→</span>
          <span className={clsx(value?.[1] != null ? "text-text-color" : "text-gray-500")}>
            {value?.[1] != null ? getInputValue(value[1]) : "DD Month, YYYY"}
          </span>
        </div>
      }
    >
      <Calendar.RangePicker {...{ value, monthsShow }} filter={filter} onComplete={() => setShow(false)} onSelect={onSelect} />
    </Calendar.Float>
  )
}

const DatePicker = ({ wrapperClass, error, label, required, filter, onSelect, value, placeholder }: DateSinglePickerProps) => {
  const [show, setShow] = useState(false)
  return (
    <Calendar.Float
      onShowChange={setShow}
      show={show}
      showClear={value != null}
      onClear={() => onSelect?.(void 0)}
      label={label}
      error={error}
      required={required}
      wrapperClass={wrapperClass}
      target={
        <div className="flex h-[24px] select-none items-center gap-2 text-sm">
          <span className={clsx(value != null ? "text-text-color" : "text-gray-400")}>
            {value != null ? getInputValue(value) : placeholder || tCommon("placeholder.date-dd-month-yyyy")}
          </span>
        </div>
      }
    >
      <Calendar.SinglePicker
        {...{ value, filter, error, label }}
        onSelect={val => {
          onSelect?.(val)
          setShow(false)
        }}
      />
    </Calendar.Float>
  )
}

const FormSinglePicker = <T extends FieldValues>({ control, name, required, rules, ...props }: IFormControl<T> & DateSinglePickerProps) => {
  return (
    <Controller
      {...{
        control,
        name,
        rules: { required: { value: required ?? false, message: tCommon("this-field-is-required") } }
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <DatePicker
          {...props}
          error={error?.message}
          value={value}
          onSelect={e => {
            onChange(e)
            props.onSelect?.(e)
          }}
          required={required}
        />
      )}
    />
  )
}
const FormRangePicker = <T extends FieldValues>({ control, name, required, rules, ...props }: IFormControl<T> & DateRangePickerProps) => {
  return (
    <Controller
      {...{
        control,
        name,
        rules: { required: { value: required ?? false, message: tCommon("this-field-is-required") } }
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <DatePickerRange
          {...props}
          error={error?.message}
          value={value}
          onSelect={range => {
            onChange(range)
            props.onSelect?.(range)
          }}
          required={required}
        />
      )}
    />
  )
}

DatePicker.Range = DatePickerRange
DatePicker.FormSingle = FormSinglePicker
DatePicker.FormRange = FormRangePicker

export default DatePicker
